<template>
  <div>
    <div>
      <BotEditorSubHeader
        :title="$t('botEditor.connectors.title')"
        :subtitle="$t('botEditor.connectors.subtitle')"
      />
    </div>

    <!-- Caricamento dei connettori fallito: senza questo banner la griglia mostra
         chip e toggle fabbricati dai default della vista, e l'unico segnale era un
         toast da 3 secondi visibile solo cliccando "Configura" (card #10278328553).
         La cura è deterministica, quindi la esegue il bottone, non l'utente. -->
    <Message v-if="!connectorsLoaded" severity="error" :closable="false" class="mb-6">
      <template #icon>
        <span class="material-icons !text-base" aria-hidden="true">error_outline</span>
      </template>
      <div class="flex flex-wrap items-center gap-3">
        <span>{{ $t('botEditor.connectors.connectorLoadError') }}</span>
        <Button
          data-telemetry-id="btn-retry-load-connectors"
          severity="secondary"
          size="small"
          outlined
          :label="$t('botEditor.connectors.retryButton')"
          :loading="connectorsLoading"
          @click="loadConnectors"
        />
      </div>
    </Message>

    <div class="connectors-grid">
      <div
        v-for="connectorType in availableConnectors"
        :key="connectorType.type"
        data-telemetry-id="connector-card"
        class="connector-card relative select-none"
        :class="{
          'is-enabled': isEnabled(connectorType.type),
          'is-configured': isConfigured(connectorType.type),
          'is-unavailable': !connectorType.available
        }"
      >
        <div class="card-header">
          <div class="connector-icon-container rounded-lg" :style="{ background: connectorType.color }">
            <BrandIcon
              v-if="connectorType.iconType === 'brand'"
              :name="connectorType.icon"
              class="w-7 h-7"
            />
            <span v-else class="material-icons !text-3xl">{{ connectorType.icon }}</span>
          </div>
          <div class="connector-info">
            <h3>{{ connectorType.name }}</h3>
            <Chip
              :label="getStatusText(connectorType.type)"
              :class="['!text-xs !font-medium', getStatusClass(connectorType.type)]"
            />
          </div>
          <!-- ToggleSwitch tiene uno stato interno e si riallinea solo quando cambia
               :model-value. Se il PATCH fallisce il modello resta com'è, quindi il catch
               incrementa il nonce nella key: lo switch si rimonta sullo stato reale. -->
          <ToggleSwitch
            :key="`${connectorType.type}-${switchNonce[connectorType.type] || 0}`"
            :model-value="isEnabled(connectorType.type)"
            :aria-label="connectorType.name"
            :disabled="!isConfigured(connectorType.type) && connectorType.type !== 'web' && connectorType.type !== 'livechat'"
            data-telemetry-id="btn-toggle-connector"
            class="shrink-0"
            @click="onConnectorSwitchClick(connectorType.type, $event)"
            @update:model-value="(enabled) => toggleConnector(connectorType.type, enabled)"
          />
        </div>

        <div class="card-body">
          <p class="connector-description">{{ connectorType.description }}</p>
          <Message
            v-if="connectorType.unavailableButActive"
            severity="warn"
            size="small"
            variant="simple"
            :closable="false"
            :pt="{ root: { role: 'status', 'aria-live': 'polite' } }"
            class="mt-2"
          >
            {{ $t('botEditor.connectors.unavailableButActive') }}
          </Message>
          <!-- Configurazione di avvio vuota su un connettore attivo: il bot non fa la
               prima mossa e un flusso iniziale che idrata il contesto non parte.
               `variant="simple"` = nessun fill né bordo dentro la card, ma stesso
               vocabolario (e stesso token) degli altri avvisi della superficie. -->
          <Message
            v-if="hasEmptyStartConfig(connectorType.type)"
            severity="warn"
            size="small"
            variant="simple"
            :closable="false"
            :pt="{ root: { role: 'status', 'aria-live': 'polite' } }"
            class="connector-start-warning"
          >
            <template #icon>
              <span class="material-icons !text-base" aria-hidden="true">warning_amber</span>
            </template>
            {{ $t('botEditor.connectors.emptyStartConfigBadge') }}
          </Message>
          <template v-if="connectorType.type === 'whatsapp' && whatsappStatus && whatsappStatus.status === 'active'">
            <div class="flex flex-col gap-2">
              <Button
                data-telemetry-id="btn-manage-whatsapp-templates"
                severity="primary"
                class="w-full"
                @click="goToTemplates"
              >
                <span class="material-icons">forum</span>
                {{ $t('botEditor.connectors.whatsappActivation.manageTemplates') }}
              </Button>
              <Button
                data-telemetry-id="btn-whatsapp-settings"
                severity="secondary"
                outlined
                class="w-full"
                @click="openConfigModal(connectorType.type)"
              >
                <span class="material-icons">settings</span>
                {{ $t('botEditor.connectors.whatsappActivation.settingsButton') }}
              </Button>
              <WhatsappCostNotice />
            </div>
          </template>
          <Button
            v-else
            data-telemetry-id="btn-configure-connector"
            severity="primary"
            class="w-full"
            @click="openConfigModal(connectorType.type)"
          >
            <span class="material-icons">settings</span>
            {{ isConfigured(connectorType.type) ? $t('botEditor.connectors.reconfigureButton') : $t('botEditor.connectors.configureButton') }}
          </Button>
        </div>

        <!-- Coming Soon Overlay -->
        <div v-if="!connectorType.available" class="coming-soon-overlay">
          <div class="flex flex-col items-center gap-2 p-5 text-gray-100 opacity-90">
            <span class="material-icons !text-4xl">schedule</span>
            <span class="text-xl uppercase font-semibold tracking-wide">{{ $t('botEditor.connectors.comingSoon') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Configuration Dialog -->
    <Dialog
      v-model:visible="showConfigModal"
      modal
      :closable="true"
      :dismissableMask="true"
      :draggable="false"
      :style="{ width: '600px' }"
    >
      <!-- Dialog Header -->
      <template #header>
        <div class="flex items-center gap-3">
          <BrandIcon
            v-if="currentConnector.iconType === 'brand'"
            :name="currentConnector.icon"
            class="w-6 h-6"
          />
          <span v-else class="material-icons !text-3xl">{{ currentConnector.icon }}</span>
          <span>{{ $t('botEditor.connectors.configModal.titlePrefix') }} {{ currentConnector.name }}</span>
        </div>
      </template>

      <!-- Divider -->
      <Divider class="divider-below" />

      <!-- Dialog Body -->
      <div class="flex flex-col gap-6">
        <!-- Comportamento Conversazione -->
        <div class="flex flex-col gap-4">
          <h4 class="text-lg font-semibold">{{ $t('botEditor.connectors.configModal.conversationSection') }}</h4>

          <!-- Due assi indipendenti, schema invariato:
               · Modalità (mode) = QUANDO il bot agisce: proattiva (prima del messaggio utente)
                 o reattiva (dopo il primo messaggio utente).
               · Contenuto (XOR) = COSA: un messaggio (welcomeMessage) o un flusso (initialFlowId). -->

          <!-- Modalità -->
          <div class="flex flex-col gap-2">
            <label class="font-medium">{{ $t('botEditor.connectors.configModal.modeLabel') }}</label>
            <div class="flex gap-6">
              <div class="flex items-center gap-2">
                <RadioButton v-model="currentConfig.conversationConfig.mode" inputId="mode-proactive" value="proactive" />
                <label for="mode-proactive" class="cursor-pointer">{{ $t('botEditor.connectors.configModal.modeProactive') }}</label>
              </div>
              <div class="flex items-center gap-2">
                <RadioButton v-model="currentConfig.conversationConfig.mode" inputId="mode-reactive" value="reactive" />
                <label for="mode-reactive" class="cursor-pointer">{{ $t('botEditor.connectors.configModal.modeReactive') }}</label>
              </div>
            </div>
            <Message severity="secondary" variant="simple" size="small" :closable="false">
              {{ currentConfig.conversationConfig.mode === 'proactive'
                 ? $t('botEditor.connectors.configModal.modeProactiveHelp')
                 : $t('botEditor.connectors.configModal.modeReactiveHelp') }}
            </Message>
          </div>

          <!-- Contenuto: messaggio XOR flusso -->
          <div class="flex flex-col gap-2">
            <label class="font-medium">{{ $t('botEditor.connectors.configModal.contentLabel') }}</label>
            <div class="flex gap-6">
              <div class="flex items-center gap-2">
                <RadioButton v-model="contentType" inputId="content-message" value="message" />
                <label for="content-message" class="cursor-pointer">{{ $t('botEditor.connectors.configModal.contentMessage') }}</label>
              </div>
              <div class="flex items-center gap-2">
                <RadioButton v-model="contentType" inputId="content-flow" value="flow" />
                <label for="content-flow" class="cursor-pointer">{{ $t('botEditor.connectors.configModal.contentFlow') }}</label>
              </div>
            </div>

            <Transition
              mode="out-in"
              enter-active-class="transition-opacity duration-150 ease-out motion-reduce:transition-none"
              enter-from-class="opacity-0"
              leave-active-class="transition-opacity duration-100 ease-out motion-reduce:transition-none"
              leave-to-class="opacity-0"
            >
              <!-- Messaggio -->
              <div v-if="contentType === 'message'" key="content-message" class="flex flex-col gap-2 mt-1">
                <Textarea
                  v-model="currentConfig.conversationConfig.welcomeMessage"
                  :placeholder="$t('botEditor.connectors.configModal.welcomeMessagePlaceholder')"
                  rows="3"
                  autoResize
                />
              </div>

              <!-- Flusso -->
              <div v-else key="content-flow" class="flex flex-col gap-2 mt-1">
                <Select
                  v-model="currentConfig.conversationConfig.initialFlowId"
                  :aria-label="$t('botEditor.connectors.configModal.initialFlowLabel')"
                  :options="flowOptions"
                  optionLabel="label"
                  optionValue="value"
                  :placeholder="$t('botEditor.connectors.configModal.initialFlowNone')"
                />
                <Message v-if="availableFlows.length === 0" severity="warn" :closable="false">
                  {{ $t('botEditor.connectors.initialFlowHint') }}
                </Message>
                <!-- Rientro nel flusso iniziale: visibile solo a flusso scelto. -->
                <Transition
                  mode="out-in"
                  enter-active-class="transition-opacity duration-150 ease-out motion-reduce:transition-none"
                  enter-from-class="opacity-0"
                  leave-active-class="transition-opacity duration-100 ease-out motion-reduce:transition-none"
                  leave-to-class="opacity-0"
                >
                  <div
                    v-if="currentConfig.conversationConfig.initialFlowId"
                    class="flex flex-col gap-2"
                  >
                    <div class="flex items-center gap-2">
                      <ToggleSwitch
                        inputId="web-reenter-initial-flow"
                        aria-describedby="web-reenter-initial-flow-help"
                        v-model="currentConfig.conversationConfig.reenterInitialFlow"
                      />
                      <label for="web-reenter-initial-flow" class="font-medium cursor-pointer">
                        {{ $t('botEditor.connectors.reenterInitialFlow.label') }}
                      </label>
                    </div>
                    <small id="web-reenter-initial-flow-help" class="text-gray-400">{{ $t('botEditor.connectors.reenterInitialFlow.help') }}</small>
                  </div>
                </Transition>
              </div>
            </Transition>

            <Message v-if="startContentEmpty" severity="warn" :closable="false">
              <template #icon>
                <span class="material-icons !text-base" aria-hidden="true">warning_amber</span>
              </template>
              {{ $t('botEditor.connectors.emptyStartContentWarning') }}
            </Message>
          </div>
        </div>

        <!-- Prompt specifico del canale web: istruzioni aggiuntive solo per
             questo connettore, concatenate al prompt comune del bot.
             A differenza di whatsapp/voicebot, il canale web non ha un default
             testuale né validazione min-length: il campo parte vuoto/con
             quanto già salvato. -->
        <div v-if="currentConnectorType === 'web'" class="flex flex-col gap-2">
          <label for="web-channel-prompt" class="font-medium">{{ $t('botEditor.connectors.webWidget.channelPromptLabel') }}</label>
          <Textarea
            id="web-channel-prompt"
            v-model="currentConfig.channelPrompt"
            rows="6"
            autoResize
            class="w-full"
          />
          <small class="text-gray-500">{{ $t('botEditor.connectors.webWidget.channelPromptHelp') }}</small>
        </div>

        <!-- Configurazione Tecnica (solo per connettori non-web) -->
        <div v-if="currentConnectorType !== 'web'" class="flex flex-col gap-2">
          <h4 class="text-lg font-semibold">{{ $t('botEditor.connectors.configModal.technicalConfigSection') }}</h4>
          <p class="text-sm text-gray-500">{{ $t('botEditor.connectors.configModal.technicalConfigPlaceholder') }}</p>
        </div>
      </div>

      <!-- Dialog footer -->
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button
            id="btn-cancel-connector-config"
            :label="$t('botEditor.connectors.configModal.cancelButton')"
            severity="secondary"
            @click="closeConfigModal"
          />
          <Button
            id="btn-save-connector-config"
            @click="saveConnectorConfig"
            severity="primary"
          >
            <span class="material-icons">save</span>
            {{ $t('botEditor.connectors.configModal.saveButton') }}
          </Button>
        </div>
      </template>
    </Dialog>

    <!-- WhatsApp Activation Dialog -->
    <Dialog
      v-model:visible="showWhatsappDialog"
      modal
      :closable="true"
      :dismissableMask="true"
      :draggable="false"
      :style="{ width: '560px' }"
    >
      <!-- Header -->
      <template #header>
        <div class="flex items-center gap-3">
          <BrandIcon name="whatsapp" class="w-6 h-6" />
          <span>{{ $t('botEditor.connectors.whatsapp.name') }}</span>
        </div>
      </template>

      <Divider class="divider-below" />

      <!-- Body -->
      <div class="flex flex-col gap-5">

        <!-- state: null or 'error' → request form -->
        <template v-if="!whatsappStatus || whatsappStatus.status === 'error'">
          <p class="text-sm text-gray-300">{{ $t('botEditor.connectors.whatsappActivation.enablePrompt') }}</p>

          <div class="flex flex-col gap-2">
            <label class="font-medium">{{ $t('botEditor.connectors.whatsappActivation.phoneLabel') }}</label>
            <PhoneNumberInput
              v-model="whatsappPhoneInput"
              :country-label="$t('botEditor.connectors.whatsappActivation.countryLabel')"
              :search-placeholder="$t('botEditor.connectors.whatsappActivation.countrySearchPlaceholder')"
              :national-number-placeholder="$t('botEditor.connectors.whatsappActivation.nationalNumberPlaceholder')"
            />
            <Message
              v-if="whatsappPhoneError"
              severity="error"
              variant="simple"
              size="small"
              :closable="false"
            >
              {{ whatsappPhoneError }}
            </Message>
          </div>
        </template>

        <!-- state: 'requested' or 'awaiting_credentials' → pending instructions -->
        <template v-else-if="whatsappStatus.status === 'requested' || whatsappStatus.status === 'awaiting_credentials'">
          <div class="flex items-center gap-3">
            <Tag
              :value="$t('botEditor.connectors.whatsappActivation.statusBadge.' + whatsappStatus.status)"
              severity="info"
            />
            <span class="text-sm text-gray-300">{{ whatsappStatus.phoneNumber }}</span>
          </div>
          <p class="text-sm text-gray-300">{{ $t('botEditor.connectors.whatsappActivation.mittoInstructions') }}</p>
          <a
            href="https://whatsappbusiness.mitto.ch/"
            target="_blank"
            rel="noopener"
            class="text-blue-400 underline text-sm hover:text-blue-300"
          >
            {{ $t('botEditor.connectors.whatsappActivation.mittoLinkText') }}
          </a>
        </template>

        <!-- state: 'active' → active info -->
        <template v-else-if="whatsappStatus.status === 'active'">
          <div class="flex items-center gap-3">
            <Tag
              :value="$t('botEditor.connectors.whatsappActivation.statusBadge.active')"
              severity="success"
            />
            <span class="text-sm text-gray-300">{{ whatsappStatus.phoneNumber }}</span>
          </div>
        </template>

        <!-- Flusso iniziale (solo a WhatsApp attivo).
             WhatsApp è sempre reattivo: niente modalità né messaggio di benvenuto,
             solo l'eventuale flusso da avviare al primo messaggio dell'utente.
             Default "Non abilitato" (nessun flusso → intent recognition). -->
        <template v-if="whatsappStatus && whatsappStatus.status === 'active'">
          <Divider class="divider-flush" />
          <div class="flex flex-col gap-2">
            <label for="wa-initial-flow" id="wa-initial-flow-label" class="font-medium">{{ $t('botEditor.connectors.whatsappActivation.initialFlowLabel') }}</label>
            <Select
              label-id="wa-initial-flow"
              aria-labelledby="wa-initial-flow-label"
              v-model="currentConfig.conversationConfig.initialFlowId"
              :options="whatsappFlowOptions"
              optionLabel="label"
              optionValue="value"
              optionDisabled="disabled"
              class="w-full"
            />
            <small class="text-gray-500">{{ $t('botEditor.connectors.whatsappActivation.initialFlowHelp') }}</small>
            <Message v-if="availableFlows.length === 0" severity="warn" :closable="false">
              {{ $t('botEditor.connectors.initialFlowHint') }}
            </Message>
            <!-- Rientro nel flusso iniziale: visibile solo a flusso scelto. -->
            <Transition
              mode="out-in"
              enter-active-class="transition-opacity duration-150 ease-out motion-reduce:transition-none"
              enter-from-class="opacity-0"
              leave-active-class="transition-opacity duration-100 ease-out motion-reduce:transition-none"
              leave-to-class="opacity-0"
            >
              <div
                v-if="currentConfig.conversationConfig.initialFlowId"
                class="flex flex-col gap-2"
              >
                <div class="flex items-center gap-2">
                  <ToggleSwitch
                    inputId="wa-reenter-initial-flow"
                    aria-describedby="wa-reenter-initial-flow-help"
                    v-model="currentConfig.conversationConfig.reenterInitialFlow"
                  />
                  <label for="wa-reenter-initial-flow" class="font-medium cursor-pointer">
                    {{ $t('botEditor.connectors.reenterInitialFlow.label') }}
                  </label>
                </div>
                <small id="wa-reenter-initial-flow-help" class="text-gray-400">{{ $t('botEditor.connectors.reenterInitialFlow.help') }}</small>
              </div>
            </Transition>
          </div>
        </template>

        <!-- Prompt specifico del canale WhatsApp: istruzioni aggiuntive solo per
             questo connettore, concatenate al prompt comune del bot. -->
        <template v-if="whatsappStatus && whatsappStatus.status === 'active'">
          <Divider class="divider-flush" />
          <div class="flex flex-col gap-2">
            <label for="wa-channel-prompt" class="font-medium">{{ $t('botEditor.connectors.whatsapp.channelPromptLabel') }}</label>
            <Textarea
              id="wa-channel-prompt"
              v-model="currentConfig.channelPrompt"
              rows="6"
              autoResize
              class="w-full"
            />
            <small v-if="!whatsappChannelPromptInvalid" class="text-gray-500">{{ $t('botEditor.connectors.whatsapp.channelPromptHelp') }}</small>
            <Message v-else severity="error" variant="simple" size="small" :closable="false">
              {{ $t('botEditor.connectors.whatsapp.channelPromptTooShort') }}
            </Message>
          </div>
        </template>

        <!-- Notifiche di consegna (delivery webhook) — solo a WhatsApp attivo.
             Endpoint del cliente che riceve gli esiti delivered/failed/read. -->
        <template v-if="whatsappStatus && whatsappStatus.status === 'active' && !deliveryWebhookUnavailable">
          <Divider class="divider-flush" />
          <div class="flex flex-col gap-2">
            <h5 class="font-medium text-gray-300">{{ $t('whatsapp.deliveryWebhook.title') }}</h5>
            <WhatsappDeliveryWebhookForm
              :fetch-config="fetchDeliveryWebhook"
              :save-config="saveDeliveryWebhookConfig"
              @load-error="onDeliveryWebhookLoadError"
            />
          </div>
        </template>

        <!-- Bot environment selector (sempre visibile quando il dialog è aperto) -->
        <div class="flex flex-col gap-2">
          <label for="wa-bot-environment" id="wa-bot-environment-label" class="font-medium">{{ $t('botEditor.connectors.configModal.botEnvironmentLabel') }}</label>
          <Select
            label-id="wa-bot-environment"
            aria-labelledby="wa-bot-environment-label"
            v-model="currentConfig.config.botEnvironment"
            :options="botEnvironmentOptions"
            optionLabel="label"
            optionValue="value"
            optionDisabled="disabled"
            class="w-full"
          />
          <small v-if="!productionBotId" class="text-gray-500">
            {{ $t('botEditor.connectors.configModal.botEnvironmentNotDeployed') }}
          </small>
        </div>

        <!-- Credit cost notice (only when WhatsApp billing is enabled) -->
        <WhatsappCostNotice />

      </div>

      <!-- Footer -->
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button
            :label="$t('botEditor.connectors.configModal.cancelButton')"
            severity="secondary"
            @click="closeWhatsappDialog"
          />
          <Button
            v-if="!whatsappStatus || whatsappStatus.status === 'error'"
            severity="primary"
            :label="$t('botEditor.connectors.whatsappActivation.requestButton')"
            :loading="whatsappLoading"
            :disabled="whatsappLoading"
            @click="requestWhatsapp"
          >
            <template #icon>
              <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">send</span>
            </template>
          </Button>
          <Button
            v-if="whatsappStatus && whatsappStatus.status !== 'error'"
            severity="primary"
            :disabled="whatsappChannelPromptInvalid"
            @click="saveWhatsappConfig"
          >
            <span class="material-icons">save</span>
            {{ $t('botEditor.connectors.configModal.saveButton') }}
          </Button>
        </div>
      </template>
    </Dialog>

    <!-- Voice (Vapi) Activation Dialog -->
    <Dialog
      v-model:visible="showVoiceDialog"
      modal
      :closable="true"
      :dismissableMask="true"
      :draggable="false"
      :style="{ width: '560px' }"
    >
      <!-- Header -->
      <template #header>
        <div class="flex items-center gap-3">
          <span class="material-icons !text-3xl">mic</span>
          <span>{{ $t('botEditor.connectors.voicebot.name') }}</span>
        </div>
      </template>

      <Divider class="divider-below" />

      <!-- Body -->
      <div class="flex flex-col gap-5">

        <!-- Readiness check in progress -->
        <template v-if="voiceAvailabilityLoading">
          <Message severity="secondary" variant="simple" size="small" :closable="false">
            {{ $t('common.loading') }}
          </Message>
        </template>

        <!-- Vapi not ready: disabled panel with explanatory message -->
        <template v-else-if="!voiceReady">
          <Message severity="warn" :closable="false">
            {{ $t('botEditor.connectors.voicebot.notReady') }}
          </Message>
        </template>

        <!-- Vapi ready: activation status + config form -->
        <template v-else>
          <template v-if="isVoiceActive">
            <div class="flex items-center gap-3">
              <Tag :value="$t('botEditor.connectors.voicebot.statusActive')" severity="success" />
            </div>
            <VoiceCostNotice />
            <Divider class="divider-flush" />
          </template>

          <div class="flex flex-col gap-2">
            <label for="voice-voice-id" id="voice-voice-id-label" class="font-medium">{{ $t('botEditor.connectors.voicebot.voiceLabel') }}</label>
            <Select
              label-id="voice-voice-id"
              aria-labelledby="voice-voice-id-label"
              v-model="selectedVoiceKey"
              :options="voiceGroups"
              optionLabel="label"
              optionValue="value"
              optionGroupLabel="label"
              optionGroupChildren="items"
              :loading="voicesLoading"
              :disabled="voicesLoading"
              class="w-full"
            />
            <small v-if="voiceCurrent && voiceCurrent.inCatalog === false" class="text-gray-500">
              {{ $t('botEditor.connectors.voicebot.voiceOnVapiHelp') }}
            </small>
            <!-- Se la lettura della voce corrente da Vapi fallisce, il select mostra
                 quella salvata in piattaforma: salvare in questa condizione farebbe
                 credere al backend che l'utente l'abbia cambiata, sovrascrivendo la
                 voce tarata a mano su Vapi. Il salvataggio va quindi bloccato finché
                 non si riesce a leggerla. -->
            <Message
              v-if="isVoiceActive && voiceCurrent === null && !voicesLoading"
              severity="warn"
              variant="simple"
              size="small"
              :closable="false"
            >
              {{ $t('botEditor.connectors.voicebot.voiceCurrentUnavailableWarning') }}
            </Message>
          </div>

          <!-- Frasi interlocutorie: le frasi stanno sul profilo Vapi, qui si
               sceglie solo se questo bot le usa. Il blocco non compare se il
               profilo non ne ha nessuna. -->
          <div v-if="voiceFillerAvailable" class="flex flex-col gap-2">
            <div class="flex items-center gap-2">
              <ToggleSwitch inputId="voice-filler-enabled" v-model="voiceFillerEnabled" />
              <label for="voice-filler-enabled" class="font-medium cursor-pointer">
                {{ $t('botEditor.connectors.voicebot.fillerLabel') }}
              </label>
            </div>
            <small class="text-gray-500">{{ $t('botEditor.connectors.voicebot.fillerHelp') }}</small>
            <Message v-if="voiceFillerWarn" severity="warn" variant="simple" size="small" :closable="false">
              {{ $t('botEditor.connectors.voicebot.fillerVoiceWarning') }}
            </Message>
          </div>

          <!-- Bot environment selector (mirrors WhatsApp's config-modal field) -->
          <div class="flex flex-col gap-2">
            <label for="voice-bot-environment" id="voice-bot-environment-label" class="font-medium">{{ $t('botEditor.connectors.configModal.botEnvironmentLabel') }}</label>
            <Select
              label-id="voice-bot-environment"
              aria-labelledby="voice-bot-environment-label"
              v-model="voiceBotEnvironment"
              :options="botEnvironmentOptions"
              optionLabel="label"
              optionValue="value"
              optionDisabled="disabled"
              class="w-full"
            />
            <small v-if="!productionBotId" class="text-gray-500">
              {{ $t('botEditor.connectors.configModal.botEnvironmentNotDeployed') }}
            </small>
          </div>

          <!-- Comportamento in apertura chiamata: nessuno / messaggio statico / flow.
               A differenza del canale web non esiste un asse "mode" separato: la
               scelta stessa determina il firstMessageMode inviato a Vapi
               (vedi voiceSyncService.resolveFirstMessage). -->
          <Divider class="divider-flush" />
          <div class="flex flex-col gap-2">
            <label class="font-medium">{{ $t('botEditor.connectors.voicebot.openingBehaviorLabel') }}</label>
            <div class="flex gap-6">
              <div class="flex items-center gap-2">
                <RadioButton v-model="voiceContentType" inputId="voice-opening-none" value="none" />
                <label for="voice-opening-none" class="cursor-pointer">{{ $t('botEditor.connectors.voicebot.openingNone') }}</label>
              </div>
              <div class="flex items-center gap-2">
                <RadioButton v-model="voiceContentType" inputId="voice-opening-message" value="message" />
                <label for="voice-opening-message" class="cursor-pointer">{{ $t('botEditor.connectors.configModal.contentMessage') }}</label>
              </div>
              <div class="flex items-center gap-2">
                <RadioButton v-model="voiceContentType" inputId="voice-opening-flow" value="flow" />
                <label for="voice-opening-flow" class="cursor-pointer">{{ $t('botEditor.connectors.configModal.contentFlow') }}</label>
              </div>
            </div>
            <Message v-if="voiceContentType === 'none'" severity="secondary" variant="simple" size="small" :closable="false">
              {{ $t('botEditor.connectors.voicebot.openingBehaviorHelp') }}
            </Message>

            <Transition
              mode="out-in"
              enter-active-class="transition-opacity duration-150 ease-out motion-reduce:transition-none"
              enter-from-class="opacity-0"
              leave-active-class="transition-opacity duration-100 ease-out motion-reduce:transition-none"
              leave-to-class="opacity-0"
            >
              <div v-if="voiceContentType === 'message'" key="voice-content-message" class="flex flex-col gap-2 mt-1">
                <Textarea
                  v-model="voiceWelcomeMessage"
                  :placeholder="$t('botEditor.connectors.configModal.welcomeMessagePlaceholder')"
                  rows="3"
                  autoResize
                />
              </div>
              <div v-else-if="voiceContentType === 'flow'" key="voice-content-flow" class="flex flex-col gap-2 mt-1">
                <Select
                  v-model="voiceInitialFlowId"
                  :aria-label="$t('botEditor.connectors.configModal.initialFlowLabel')"
                  :options="voiceFlowOptions"
                  optionLabel="label"
                  optionValue="value"
                  optionDisabled="disabled"
                  class="w-full"
                />
                <Message v-if="availableFlows.length === 0" severity="warn" :closable="false">
                  {{ $t('botEditor.connectors.initialFlowHint') }}
                </Message>
              </div>
            </Transition>
          </div>

          <!-- Prompt specifico del canale voice: istruzioni aggiuntive solo per
               questo connettore, concatenate al prompt comune del bot. -->
          <Divider class="divider-flush" />
          <div class="flex flex-col gap-2">
            <label for="voice-channel-prompt" class="font-medium">{{ $t('botEditor.connectors.voicebot.channelPromptLabel') }}</label>
            <Textarea
              id="voice-channel-prompt"
              v-model="voiceChannelPrompt"
              rows="6"
              autoResize
              class="w-full"
            />
            <small v-if="!voiceChannelPromptInvalid" class="text-gray-500">{{ $t('botEditor.connectors.voicebot.channelPromptHelp') }}</small>
            <Message v-else severity="error" variant="simple" size="small" :closable="false">
              {{ $t('botEditor.connectors.voicebot.channelPromptTooShort') }}
            </Message>
          </div>

          <!-- Test call (Prova): in-app WebRTC call to the bot's Vapi assistant -->
          <Divider class="divider-flush" />
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-3">
              <Button
                v-if="callState === 'idle'"
                severity="secondary"
                outlined
                :disabled="!canTestVoice"
                @click="startVoiceTest"
              >
                <span class="material-icons">mic</span>
                {{ $t('botEditor.connectors.voicebot.test') }}
              </Button>
              <Button
                v-else
                severity="danger"
                outlined
                :label="$t('botEditor.connectors.voicebot.endTest')"
                :loading="callState === 'connecting'"
                @click="stopVoiceTest"
              >
                <template #icon>
                  <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">call_end</span>
                </template>
              </Button>

              <Message v-if="callState === 'connecting'" severity="secondary" variant="simple" size="small" :closable="false">
                {{ $t('botEditor.connectors.voicebot.testConnecting') }}
              </Message>
              <Message v-else-if="callState === 'active'" severity="success" variant="simple" size="small" :closable="false">
                {{ $t('botEditor.connectors.voicebot.testActive') }}
              </Message>
            </div>

            <Message v-if="callState === 'idle' && testDisabledReasonKey" severity="secondary" variant="simple" size="small" :closable="false">
              {{ $t(testDisabledReasonKey) }}
            </Message>

            <div v-if="transcript.length" class="mt-2 rounded-md border border-gray-700 bg-gray-800/60">
              <p class="px-3 pt-2 pb-1 text-xs font-medium text-gray-400 border-b border-gray-700/70">
                {{ $t('botEditor.connectors.voicebot.transcriptLabel') }}
              </p>
              <div class="flex flex-col gap-1 max-h-40 overflow-y-auto p-3">
                <p v-for="(entry, idx) in transcript" :key="idx" class="text-sm text-gray-300">
                  <span class="font-semibold">{{ entry.role }}:</span> {{ entry.text }}
                </p>
              </div>
            </div>
          </div>

          <!-- Telephony (SIP trunk / Twilio number linking) — solo a connettore attivo -->
          <template v-if="isVoiceActive">
            <Divider class="divider-flush" />
            <div class="flex flex-col gap-4">
              <h4 class="text-lg font-semibold">{{ $t('botEditor.connectors.voicebot.telephonySection') }}</h4>

              <div v-if="telephonyLinkedNumber" class="flex items-center gap-3">
                <Tag :value="$t('botEditor.connectors.voicebot.linkedNumber')" severity="success" />
                <span class="text-sm text-gray-300">{{ telephonyLinkedNumber }} ({{ telephonyLinkedProviderLabel }})</span>
              </div>

              <div class="flex flex-col gap-2">
                <label for="telephony-provider" id="telephony-provider-label" class="font-medium">{{ $t('botEditor.connectors.voicebot.telephonyProviderLabel') }}</label>
                <Select
                  label-id="telephony-provider"
                  aria-labelledby="telephony-provider-label"
                  v-model="telephonyProvider"
                  :options="telephonyProviderOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                />
              </div>

              <template v-if="telephonyProvider === 'sip'">
                <div class="flex flex-col gap-2">
                  <label for="telephony-sip-gateway" class="font-medium">{{ $t('botEditor.connectors.voicebot.sipGatewayLabel') }}</label>
                  <InputText id="telephony-sip-gateway" v-model="telephonySipGateway" :placeholder="$t('botEditor.connectors.voicebot.sipGatewayPlaceholder')" class="w-full" />
                </div>
                <div class="flex flex-col gap-2">
                  <label for="telephony-sip-username" class="font-medium">{{ $t('botEditor.connectors.voicebot.sipUsernameLabel') }}</label>
                  <InputText id="telephony-sip-username" v-model="telephonySipUsername" :placeholder="$t('botEditor.connectors.voicebot.sipUsernamePlaceholder')" class="w-full" />
                </div>
                <div class="flex flex-col gap-2">
                  <label for="telephony-sip-password" class="font-medium">{{ $t('botEditor.connectors.voicebot.sipPasswordLabel') }}</label>
                  <Password
                    inputId="telephony-sip-password"
                    v-model="telephonySipPassword"
                    :feedback="false"
                    toggleMask
                    :placeholder="sipPasswordPlaceholder"
                    class="w-full"
                    inputClass="w-full"
                  />
                </div>
              </template>
              <template v-else>
                <div class="flex flex-col gap-2">
                  <label for="telephony-twilio-sid" class="font-medium">{{ $t('botEditor.connectors.voicebot.twilioAccountSidLabel') }}</label>
                  <InputText id="telephony-twilio-sid" v-model="telephonyTwilioAccountSid" :placeholder="$t('botEditor.connectors.voicebot.twilioAccountSidPlaceholder')" class="w-full" />
                </div>
                <div class="flex flex-col gap-2">
                  <label for="telephony-twilio-token" class="font-medium">{{ $t('botEditor.connectors.voicebot.twilioAuthTokenLabel') }}</label>
                  <Password
                    inputId="telephony-twilio-token"
                    v-model="telephonyTwilioAuthToken"
                    :feedback="false"
                    toggleMask
                    :placeholder="twilioAuthTokenPlaceholder"
                    class="w-full"
                    inputClass="w-full"
                  />
                </div>
              </template>

              <div class="flex flex-col gap-2">
                <label for="telephony-number" class="font-medium">{{ $t('botEditor.connectors.voicebot.phoneNumberLabel') }}</label>
                <InputText id="telephony-number" v-model="telephonyNumber" :placeholder="$t('botEditor.connectors.voicebot.phoneNumberPlaceholder')" class="w-full" />
              </div>

              <div class="flex gap-2">
                <Button
                  severity="primary"
                  :label="$t('botEditor.connectors.voicebot.linkNumber')"
                  :loading="telephonyActionLoading"
                  :disabled="telephonyActionLoading"
                  @click="linkTelephony"
                >
                  <template #icon>
                    <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">phone</span>
                  </template>
                </Button>
                <Button
                  v-if="telephonyLinkedNumber"
                  severity="danger"
                  outlined
                  :label="$t('botEditor.connectors.voicebot.unlinkNumber')"
                  :loading="telephonyActionLoading"
                  :disabled="telephonyActionLoading"
                  @click="unlinkTelephony"
                >
                  <template #icon>
                    <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">phone_disabled</span>
                  </template>
                </Button>
              </div>
            </div>
          </template>
        </template>
      </div>

      <!-- Footer -->
      <template #footer>
        <div class="flex items-center justify-between gap-2">
          <!-- Azione distruttiva isolata a sinistra, lontana dalle azioni primarie -->
          <div>
            <Button
              v-if="voiceReady && isVoiceActive"
              severity="danger"
              outlined
              :label="$t('botEditor.connectors.voicebot.deactivate')"
              :loading="voiceActionLoading"
              :disabled="voiceActionLoading"
              @click="deactivateVoice"
            >
              <template #icon>
                <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">mic_off</span>
              </template>
            </Button>
          </div>
          <div class="flex gap-2">
            <Button
              :label="$t('botEditor.connectors.configModal.cancelButton')"
              severity="secondary"
              @click="closeVoiceDialog"
            />
            <Button
              v-if="voiceReady && !isVoiceActive"
              severity="primary"
              :label="$t('botEditor.connectors.voicebot.activate')"
              :loading="voiceActionLoading"
              :disabled="voiceActionLoading || voiceChannelPromptInvalid || voicesLoading || !selectedVoiceKey || (isVoiceActive && voiceCurrent === null)"
              @click="activateVoice"
            >
              <template #icon>
                <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">mic</span>
              </template>
            </Button>
            <Button
              v-if="voiceReady && isVoiceActive"
              severity="primary"
              :label="$t('botEditor.connectors.voicebot.save')"
              :loading="voiceActionLoading"
              :disabled="voiceActionLoading || voiceChannelPromptInvalid || voicesLoading || !selectedVoiceKey || (isVoiceActive && voiceCurrent === null)"
              @click="activateVoice"
            >
              <template #icon>
                <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">save</span>
              </template>
            </Button>
          </div>
        </div>
      </template>
    </Dialog>

    <!-- GPT-Live Connector Dialog: config + chiamata di prova WebRTC con didascalie live -->
    <GptLiveConnectorDialog
      v-model:visible="showGptLiveDialog"
      :workspace-id="workspaceId"
      :bot-id="botId"
      :connector="gptLiveConnector"
      :flows="availableFlows"
      :production-bot-id="productionBotId"
      @saved="loadConnectors"
    />

    <!-- Live Chat: connettore con rotte dedicate (collegamento a un team dell'handover) -->
    <LiveChatConnectorDialog
      v-model:visible="showLiveChatDialog"
      :workspace-id="workspaceId"
      :bot-id="botId"
      :bot-name="botName"
      :connector="liveChatConnector"
      @saved="loadConnectors"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import Vapi from '@vapi-ai/web';
import api from '../../services/api';
import { requestWhatsappActivation, getWhatsappActivationStatus, getWhatsappDeliveryWebhook, saveWhatsappDeliveryWebhook } from '../../services/whatsappService';
import { CHANNEL_PROMPT_DEFAULTS } from '../../constants/channelPromptDefaults';
import { hasEmptyStartContent, startContentIsRequired } from '../../utils/connectorStartConfig';
import { visibleConnectors } from '../../utils/visibleConnectors';
import { buildVoiceGroups, parseVoiceKey } from '../../utils/voiceSelectOptions';
import BotEditorSubHeader from '../../components/ui/BotEditorSubHeader.vue';
import BrandIcon from '../../components/ui/BrandIcon.vue';
import PhoneNumberInput from '../../components/ui/PhoneNumberInput.vue';
import WhatsappCostNotice from '../../components/whatsapp/WhatsappCostNotice.vue';
import VoiceCostNotice from '../../components/voice/VoiceCostNotice.vue';
import WhatsappDeliveryWebhookForm from '../../components/whatsapp/WhatsappDeliveryWebhookForm.vue';
import GptLiveConnectorDialog from '../../components/connectors/GptLiveConnectorDialog.vue';
import LiveChatConnectorDialog from '../../components/connectors/LiveChatConnectorDialog.vue';

export default {
  name: 'Connectors',
  
  components: {
    WhatsappCostNotice,
    VoiceCostNotice,
    PhoneNumberInput,
    WhatsappDeliveryWebhookForm,
    GptLiveConnectorDialog,
    LiveChatConnectorDialog
  },
  
  setup() {
    const { t } = useI18n();
    const toast = useToast();
    const route = useRoute();
    const router = useRouter();
    const botId = route.params.id;
    const workspaceId = route.params.workspaceId;

    const connectors = ref([]);
    // Tipi di connettore abilitati dall'admin per questo workspace (card #10340678981).
    // undefined = backend non ancora interrogato o troppo vecchio per dichiararli: in
    // quel caso visibleConnectors() mostra tutto come prima della regola.
    const availableTypes = ref(undefined);
    // I connettori sono stati letti davvero dal backend? Se la GET fallisce, `connectors`
    // resta vuoto e "connettore non trovato" diventa indistinguibile da "connettore mai
    // configurato": aprire la modal su quei default e salvarli sovrascriveva la
    // configurazione reale, azzerando il flusso iniziale in silenzio (card #10278328553).
    const connectorsLoaded = ref(false);
    const connectorsLoading = ref(false);
    const availableFlows = ref([]);
    const showConfigModal = ref(false);
    const currentConnectorType = ref(null);
    const currentConfig = ref({
      conversationConfig: {
        mode: 'proactive',
        welcomeMessage: '',
        initialFlowId: null,
        reenterInitialFlow: false
      },
      config: {}
    });

    // Asse "Contenuto" (UI): messaggio XOR flusso. Derivato dallo schema (initialFlowId
    // presente → 'flow'); allo save azzera il campo dell'opzione non scelta.
    const contentType = ref('message');
    const syncContentTypeFromConfig = () => {
      contentType.value = currentConfig.value.conversationConfig?.initialFlowId ? 'flow' : 'message';
    };
    const applyContentXor = () => {
      const cc = currentConfig.value.conversationConfig;
      if (!cc) return;
      if (contentType.value === 'message') {
        cc.initialFlowId = null;
        // Un toggle attivo senza flusso è incoerente: il backend lo ignorerebbe in
        // silenzio, ma è più pulito azzerarlo esplicitamente al lato UI.
        cc.reenterInitialFlow = false;
      } else {
        cc.welcomeMessage = '';
      }
    };

    // Se il flusso iniziale viene rimosso, azzera il toggle: uno stato acceso senza
    // flusso è incoerente e ricomparirebbe acceso alla riselezione successiva.
    watch(
      () => currentConfig.value.conversationConfig?.initialFlowId,
      (newId) => {
        if (!newId && currentConfig.value.conversationConfig) {
          currentConfig.value.conversationConfig.reenterInitialFlow = false;
        }
      }
    );

    // Presidio contro il fallimento silenzioso (card #10278328553): un connettore senza
    // né messaggio né flusso iniziale non fa la prima mossa e, se il bot idratava il
    // contesto da un flusso iniziale, serve ogni visitatore come anonimo senza dirlo.
    // Nella modal si valuta il solo lato attivo dell'XOR, perché il campo dell'opzione
    // non scelta viene azzerato solo al salvataggio.
    const startContentEmpty = computed(() => {
      const cc = currentConfig.value.conversationConfig;
      if (!cc) return false;
      return hasEmptyStartContent({
        welcomeMessage: contentType.value === 'message' ? cc.welcomeMessage : '',
        initialFlowId: contentType.value === 'flow' ? cc.initialFlowId : null,
      });
    });

    // Stesso presidio sulla griglia dei connettori, così la configurazione rotta si vede
    // senza aprire la modal: solo per i connettori attivi e realmente caricati.
    const hasEmptyStartConfig = (type) => {
      if (!connectorsLoaded.value) return false;
      if (!startContentIsRequired(type)) return false;
      const connector = connectors.value.find(c => c.type === type);
      if (!connector || !connector.enabled) return false;
      return hasEmptyStartContent(connector.conversationConfig);
    };

    // --- Bot environment state (per connettore WhatsApp) ---
    const productionBotId = ref(null);
    const botName = ref('');

    const botEnvironmentOptions = computed(() => [
      { label: t('botEditor.connectors.configModal.botEnvironmentPreview'), value: 'preview' },
      { label: t('botEditor.connectors.configModal.botEnvironmentProduction'), value: 'production', disabled: !productionBotId.value },
    ]);

    const loadBotData = async () => {
      try {
        const response = await api.get(`/workspaces/${workspaceId}/bots/${botId}`);
        productionBotId.value = response.data?.data?.productionBotId || null;
        botName.value = response.data?.data?.name || '';
      } catch (e) {
        Console.error('[Connectors] Error loading bot data for connector env:', e);
      }
    };
    // --- end Bot environment state ---

    // --- WhatsApp activation state ---
    const showWhatsappDialog = ref(false);
    const whatsappStatus = ref(null);      // null | { status, phoneNumber, provider }
    const whatsappPhoneInput = ref('');
    const whatsappPhoneError = ref('');
    const whatsappLoading = ref(false);

    const loadWhatsappStatus = async () => {
      try {
        const result = await getWhatsappActivationStatus(workspaceId, botId);
        whatsappStatus.value = result.data || null;
      } catch (error) {
        Console.error('[Connectors] Error loading WhatsApp status:', error);
        whatsappStatus.value = null;
      }
    };

    // Delivery webhook (sezione Configurazione tecnica del connettore WhatsApp)
    const deliveryWebhookUnavailable = ref(false);
    const fetchDeliveryWebhook = async () => {
      const res = await getWhatsappDeliveryWebhook(workspaceId, botId);
      return res?.data || {};
    };
    const saveDeliveryWebhookConfig = async (payload) => {
      const res = await saveWhatsappDeliveryWebhook(workspaceId, botId, payload);
      return res?.data || null;
    };
    const onDeliveryWebhookLoadError = (err) => {
      // Connettore sparito sotto i piedi: la sezione si nasconde (da spec).
      if (err?.response?.status === 404) deliveryWebhookUnavailable.value = true;
    };

    const openWhatsappDialog = async () => {
      whatsappPhoneInput.value = '';
      whatsappPhoneError.value = '';
      // Reset: un 404 di una sessione precedente non deve nascondere la sezione per sempre.
      deliveryWebhookUnavailable.value = false;
      // Carica il connector WhatsApp in currentConfig per gestire botEnvironment
      // e la sezione "Comportamento Conversazione" (mode / messaggio XOR flusso).
      currentConnectorType.value = 'whatsapp';
      // Ricarica i flussi per popolare il Select con dati freschi (come openConfigModal)
      await loadFlows();
      const existing = connectors.value.find(c => c.type === 'whatsapp');
      if (existing) {
        currentConfig.value = JSON.parse(JSON.stringify(existing));
        // initialFlowId: MongoDB ritorna un ObjectId che dopo JSON.stringify diventa
        // un oggetto → normalizza a stringa così il Select lo matcha (optionValue).
        if (currentConfig.value.conversationConfig?.initialFlowId) {
          const id = currentConfig.value.conversationConfig.initialFlowId;
          if (typeof id === 'object' && id !== null) {
            currentConfig.value.conversationConfig.initialFlowId = id._id || id.toString?.() || '';
          } else {
            currentConfig.value.conversationConfig.initialFlowId = String(id);
          }
        }
        if (!currentConfig.value.config) currentConfig.value.config = {};
        if (!currentConfig.value.config.botEnvironment) {
          currentConfig.value.config.botEnvironment = 'preview';
        }
        if (!currentConfig.value.channelPrompt) {
          currentConfig.value.channelPrompt = CHANNEL_PROMPT_DEFAULTS.whatsapp;
        }
      } else {
        currentConfig.value = {
          type: 'whatsapp',
          enabled: false,
          conversationConfig: { mode: 'reactive', welcomeMessage: '', initialFlowId: null, reenterInitialFlow: false },
          config: { botEnvironment: 'preview' },
          channelPrompt: CHANNEL_PROMPT_DEFAULTS.whatsapp
        };
      }
      await loadWhatsappStatus();
      showWhatsappDialog.value = true;
    };

    const closeWhatsappDialog = () => {
      showWhatsappDialog.value = false;
    };

    // Guardia client-side: il backend rifiuta un channelPrompt più corto di 10
    // caratteri (quando il canale ha un default non vuoto, come WhatsApp/voicebot).
    // Blocchiamo il salvataggio prima del round-trip invece di lasciare che l'utente
    // scopra l'errore solo dal toast generico.
    const whatsappChannelPromptInvalid = computed(() => {
      return !currentConfig.value.channelPrompt || currentConfig.value.channelPrompt.trim().length < 10;
    });

    // Estrae un messaggio d'errore utilizzabile dalla risposta del backend, che ha
    // forme diverse a seconda dell'endpoint: connectorRoutes.js ritorna
    // { error: '<string>' } (flat), voiceConnectorController.js ritorna
    // { error: { code, message } } (nested). Fallback a null se non c'è nulla di utile.
    const extractServerErrorMessage = (error) => {
      const errPayload = error.response?.data?.error;
      if (typeof errPayload === 'string') return errPayload;
      if (errPayload && typeof errPayload === 'object' && errPayload.message) return errPayload.message;
      return null;
    };

    const saveWhatsappConfig = async () => {
      try {
        // WhatsApp è sempre reattivo: nessun messaggio di benvenuto, solo l'eventuale flusso iniziale.
        if (!currentConfig.value.conversationConfig) currentConfig.value.conversationConfig = {};
        currentConfig.value.conversationConfig.mode = 'reactive';
        currentConfig.value.conversationConfig.welcomeMessage = '';
        await api.post(`/workspaces/${workspaceId}/bots/${botId}/connectors`, currentConfig.value);
        await loadConnectors();
        showWhatsappDialog.value = false;
        toast.add({ severity: 'success', summary: t('botEditor.connectors.toastSaved'), life: 3000 });
      } catch (error) {
        Console.error('[Connectors] Error saving WhatsApp config:', error);
        const serverMessage = extractServerErrorMessage(error);
        toast.add({ severity: 'error', summary: serverMessage || t('botEditor.connectors.toastSaveError'), life: 3000 });
      }
    };

    const requestWhatsapp = async () => {
      whatsappPhoneError.value = '';
      whatsappLoading.value = true;
      try {
        const result = await requestWhatsappActivation(workspaceId, botId, whatsappPhoneInput.value);
        if (result.success) {
          toast.add({ severity: 'success', summary: t('botEditor.connectors.whatsappActivation.requestSuccess'), life: 3000 });
          await loadWhatsappStatus();
        }
      } catch (error) {
        const code = error.response?.data?.error?.code;
        if (code === 'PHONE_INVALID' || code === 'PHONE_REQUIRED') {
          whatsappPhoneError.value = t('botEditor.connectors.whatsappActivation.errorInvalidPhone');
          toast.add({ severity: 'warn', summary: t('botEditor.connectors.whatsappActivation.errorInvalidPhone'), life: 3000 });
        } else if (await handleUnavailable(error)) {
          // gestito: toast + ricarico l'elenco
        } else {
          toast.add({ severity: 'error', summary: t('botEditor.connectors.whatsappActivation.errorGeneric'), life: 3000 });
        }
      } finally {
        whatsappLoading.value = false;
      }
    };

    // Expose activation status for the card badge
    const getWhatsappStatusClass = () => {
      const s = whatsappStatus.value?.status;
      if (s === 'active') return '!bg-green-500 !text-white';
      if (s === 'requested' || s === 'awaiting_credentials') return '!bg-blue-500 !text-blue-100';
      if (s === 'error') return '!bg-red-500 !text-white';
      return '!bg-gray-600 !text-gray-300';
    };

    const getWhatsappStatusText = () => {
      const s = whatsappStatus.value?.status;
      if (s === 'active') return t('botEditor.connectors.whatsappActivation.statusBadge.active');
      if (s === 'requested') return t('botEditor.connectors.whatsappActivation.statusBadge.requested');
      if (s === 'awaiting_credentials') return t('botEditor.connectors.whatsappActivation.statusBadge.awaiting_credentials');
      if (s === 'error') return t('botEditor.connectors.whatsappActivation.statusBadge.error');
      return t('botEditor.connectors.statusInactive');
    };
    const goToTemplates = () => {
      router.push(`/app/workspace/${workspaceId}/bots/${botId}/whatsapp-templates`);
    };

    // --- end WhatsApp activation state ---

    // --- Voice (Vapi) activation state ---
    // Mirrors the WhatsApp activation dialog: readiness check → config form →
    // activate/deactivate lifecycle, using the same `api` service and bot-scoped
    // URL convention (/workspaces/:workspaceId/bots/:botId/...).
    const showVoiceDialog = ref(false);
    const voiceReady = ref(null);           // null (unknown) | true | false
    const voiceAvailabilityLoading = ref(false);
    const voiceActionLoading = ref(false);
    const voicesLoading = ref(false);
    const selectedVoiceKey = ref(null);      // "<provider>:<voiceId>"
    const voiceGroups = ref([]);             // [{ key, label, items: [{ label, value }] }]
    const voiceCurrent = ref(null);          // { provider, voiceId, inCatalog } letta da Vapi
    const voiceFillerAvailable = ref(false); // il profilo Vapi ha frasi
    const voiceFillerEnabled = ref(true);

    // Il toggle del filler avverte quando la voce scelta è Vapi: quelle voci
    // spezzano male la frase interlocutoria (vedi spec del 2026-09-18).
    const voiceFillerWarn = computed(() => {
      return voiceFillerEnabled.value && parseVoiceKey(selectedVoiceKey.value).provider === 'vapi';
    });
    const voiceChannelPrompt = ref('');
    // Stessa guardia client-side del connettore WhatsApp (v. whatsappChannelPromptInvalid).
    const voiceChannelPromptInvalid = computed(() => {
      return !voiceChannelPrompt.value || voiceChannelPrompt.value.trim().length < 10;
    });

    // Test call ("Prova"): in-app WebRTC call to the bot's Vapi assistant via the
    // Vapi Web SDK. The SDK instance is kept in a plain (non-reactive) variable —
    // it must NOT be wrapped in ref()/reactive() to avoid Vue proxying its internals.
    const callState = ref('idle');          // 'idle' | 'connecting' | 'active'
    const transcript = ref([]);             // [{ role, text }]
    let vapiInstance = null;

    // Bot environment (preview/production) for the voice connector — mirrors the
    // WhatsApp connector's `config.botEnvironment` field/options (see above).
    const voiceBotEnvironment = ref('preview');

    // Comportamento in apertura chiamata: nessuno / messaggio statico / flow.
    const voiceContentType = ref('none'); // 'none' | 'message' | 'flow'
    const voiceWelcomeMessage = ref('');
    const voiceInitialFlowId = ref(null);

    // Come whatsappFlowOptions: stessa lista di flow, opzione "nessuno" propria del canale voce.
    const voiceFlowOptions = computed(() => {
      const options = [
        { label: t('botEditor.connectors.voicebot.initialFlowNone'), value: null },
      ];
      if (availableFlows.value.length === 0) {
        options.push({
          label: t('botEditor.connectors.configModal.noFlowsAvailable') || 'Nessun flusso disponibile',
          value: null,
          disabled: true,
        });
      } else {
        availableFlows.value.forEach((flow) => {
          options.push({ label: flow.name, value: flow._id });
        });
      }
      return options;
    });

    // --- Telephony (SIP trunk / Twilio number linking) ---
    // Lets the customer link a phone number to the already-active voice connector,
    // either via a generic SIP trunk or Twilio. Secrets (sipPassword,
    // twilioAuthToken) are never returned by the backend — fields stay blank on
    // load and a "secretSet" placeholder is shown when a secret is already stored.
    const telephonyProvider = ref('sip');
    const telephonySipGateway = ref('');
    const telephonySipUsername = ref('');
    const telephonySipPassword = ref('');
    const telephonyTwilioAccountSid = ref('');
    const telephonyTwilioAuthToken = ref('');
    const telephonyNumber = ref('');
    const telephonyActionLoading = ref(false);

    const telephonyProviderOptions = computed(() => [
      { label: t('botEditor.connectors.voicebot.providerSip'), value: 'sip' },
      { label: t('botEditor.connectors.voicebot.providerTwilio'), value: 'twilio' }
    ]);

    const telephonyLinkedNumber = computed(() => voiceConnector.value?.config?.phoneNumber || null);

    const telephonyLinkedProviderLabel = computed(() => {
      const p = voiceConnector.value?.config?.telephonyProvider;
      if (p === 'sip') return t('botEditor.connectors.voicebot.providerSip');
      if (p === 'twilio') return t('botEditor.connectors.voicebot.providerTwilio');
      return '';
    });

    // Secrets are write-only: show a placeholder instead of the real value when
    // the connector already has that provider's secret stored server-side.
    const sipPasswordPlaceholder = computed(() => {
      return voiceConnector.value?.config?.telephonyProvider === 'sip'
        ? t('botEditor.connectors.voicebot.secretSet')
        : t('botEditor.connectors.voicebot.sipPasswordPlaceholder');
    });
    const twilioAuthTokenPlaceholder = computed(() => {
      return voiceConnector.value?.config?.telephonyProvider === 'twilio'
        ? t('botEditor.connectors.voicebot.secretSet')
        : t('botEditor.connectors.voicebot.twilioAuthTokenPlaceholder');
    });

    const resetTelephonySecrets = () => {
      telephonySipPassword.value = '';
      telephonyTwilioAuthToken.value = '';
    };

    const linkTelephony = async () => {
      telephonyActionLoading.value = true;
      try {
        const body = telephonyProvider.value === 'sip'
          ? {
              provider: 'sip',
              gateway: telephonySipGateway.value,
              sipUsername: telephonySipUsername.value,
              sipPassword: telephonySipPassword.value,
              number: telephonyNumber.value
            }
          : {
              provider: 'twilio',
              twilioAccountSid: telephonyTwilioAccountSid.value,
              twilioAuthToken: telephonyTwilioAuthToken.value,
              number: telephonyNumber.value
            };
        await api.post(`/workspaces/${workspaceId}/bots/${botId}/voice/telephony`, body);
        await loadConnectors();
        resetTelephonySecrets();
        toast.add({ severity: 'success', summary: t('botEditor.connectors.voicebot.telephonyLinked'), life: 3000 });
      } catch (error) {
        Console.error('[Connectors] Error linking telephony:', error);
        toast.add({ severity: 'error', summary: t('botEditor.connectors.voicebot.telephonyError'), life: 3000 });
      } finally {
        telephonyActionLoading.value = false;
      }
    };

    const unlinkTelephony = async () => {
      telephonyActionLoading.value = true;
      try {
        await api.post(`/workspaces/${workspaceId}/bots/${botId}/voice/telephony/remove`);
        await loadConnectors();
        telephonyNumber.value = '';
        telephonySipGateway.value = '';
        telephonySipUsername.value = '';
        telephonyTwilioAccountSid.value = '';
        resetTelephonySecrets();
        toast.add({ severity: 'success', summary: t('botEditor.connectors.toastDeactivated'), life: 3000 });
      } catch (error) {
        Console.error('[Connectors] Error unlinking telephony:', error);
        toast.add({ severity: 'error', summary: t('botEditor.connectors.voicebot.telephonyError'), life: 3000 });
      } finally {
        telephonyActionLoading.value = false;
      }
    };
    // --- end Telephony state ---

    // The BotConnector doc (type "voicebot") is created/updated by the backend on
    // activate/deactivate and surfaced via the generic connectors list (same as
    // every other connector type) — no dedicated status endpoint is needed.
    const voiceConnector = computed(() => connectors.value.find(c => c.type === 'voicebot'));
    const isVoiceActive = computed(() => voiceConnector.value?.enabled === true);

    // --- GPT-Live activation dialog (dedicated component, see GptLiveConnectorDialog.vue) ---
    const showGptLiveDialog = ref(false);
    const gptLiveConnector = computed(() => connectors.value.find((c) => c.type === 'gptlive') || null);

    // --- Live Chat dialog (dedicated component, see LiveChatConnectorDialog.vue) ---
    const showLiveChatDialog = ref(false);
    const liveChatConnector = computed(() => connectors.value.find((c) => c.type === 'livechat') || null);

    // Curated Azure voices for the bot's language, fetched on dialog open.
    // Provider/language/first message are fixed backend-side (provider) or
    // derived from the bot (language, welcome message) — only the voice is
    // user-selectable.
    const loadVoiceOptions = async () => {
      voicesLoading.value = true;
      try {
        const response = await api.get(`/workspaces/${workspaceId}/bots/${botId}/voice/voices`);
        const data = response.data?.data || {};
        voiceCurrent.value = data.current || null;
        const { groups, selected } = buildVoiceGroups({
          voices: data.voices || [],
          current: data.current || null,
          savedProvider: voiceConnector.value?.config?.voiceProvider || '',
          savedVoiceId: voiceConnector.value?.config?.voiceId || '',
          remoteGroupLabel: t('botEditor.connectors.voicebot.voiceOnVapiGroup'),
        });
        voiceGroups.value = groups;
        selectedVoiceKey.value = selected;
      } catch (error) {
        Console.error('[Connectors] Error loading voices:', error);
        voiceGroups.value = [];
        voiceCurrent.value = null;
      } finally {
        voicesLoading.value = false;
      }
    };

    const openVoiceDialog = async () => {
      currentConnectorType.value = 'voicebot';
      voiceReady.value = null;
      voiceAvailabilityLoading.value = true;
      voiceGroups.value = [];
      voiceCurrent.value = null;
      selectedVoiceKey.value = null;
      voiceFillerEnabled.value = voiceConnector.value?.config?.fillerEnabled !== false;
      voiceChannelPrompt.value = voiceConnector.value?.channelPrompt || CHANNEL_PROMPT_DEFAULTS.voicebot;
      voiceBotEnvironment.value = voiceConnector.value?.config?.botEnvironment || 'preview';
      voiceWelcomeMessage.value = voiceConnector.value?.conversationConfig?.welcomeMessage || '';
      // initialFlowId: MongoDB ritorna un ObjectId popolato ({_id, name}) → normalizza
      // a stringa così il Select lo matcha (optionValue), come in openWhatsappDialog.
      {
        const rawFlowId = voiceConnector.value?.conversationConfig?.initialFlowId;
        if (rawFlowId && typeof rawFlowId === 'object') {
          voiceInitialFlowId.value = rawFlowId._id || rawFlowId.toString?.() || null;
        } else {
          voiceInitialFlowId.value = rawFlowId || null;
        }
      }
      voiceContentType.value = voiceInitialFlowId.value ? 'flow' : (voiceWelcomeMessage.value ? 'message' : 'none');
      transcript.value = [];
      callState.value = 'idle';

      // Telephony: prefill non-secret fields from the connector config; secrets
      // (sipPassword, twilioAuthToken) are never returned by the backend and stay blank.
      telephonyProvider.value = voiceConnector.value?.config?.telephonyProvider || 'sip';
      telephonySipGateway.value = voiceConnector.value?.config?.sipGateway || '';
      telephonySipUsername.value = voiceConnector.value?.config?.sipUsername || '';
      telephonyTwilioAccountSid.value = voiceConnector.value?.config?.twilioAccountSid || '';
      telephonyNumber.value = voiceConnector.value?.config?.phoneNumber || '';
      resetTelephonySecrets();

      // productionBotId is loaded once in onMounted (loadBotData) and shared across
      // connectors (WhatsApp, voice) — no extra fetch needed here.
      showVoiceDialog.value = true;

      try {
        const response = await api.get(`/workspaces/${workspaceId}/bots/${botId}/voice/availability`);
        voiceReady.value = !!response.data?.data?.ready;
        voiceFillerAvailable.value = !!response.data?.data?.fillerAvailable;
      } catch (error) {
        Console.error('[Connectors] Error checking voice availability:', error);
        voiceReady.value = false;
      } finally {
        voiceAvailabilityLoading.value = false;
      }

      if (voiceReady.value) {
        await loadVoiceOptions();
      }
    };

    const closeVoiceDialog = () => {
      teardownVoiceTest();
      transcript.value = [];
      showVoiceDialog.value = false;
    };

    const activateVoice = async () => {
      voiceActionLoading.value = true;
      try {
        const voice = parseVoiceKey(selectedVoiceKey.value);
        await api.post(`/workspaces/${workspaceId}/bots/${botId}/voice/activate`, {
          voiceProvider: voice.provider,
          voiceId: voice.voiceId,
          // Il toggle è nascosto quando il profilo non ha frasi: in quel caso non
          // va mandato nulla, altrimenti ogni salvataggio scriverebbe sempre
          // fillerEnabled:true e cancellerebbe un false impostato dalla migrazione
          // (o da un salvataggio precedente fatto quando le frasi c'erano).
          ...(voiceFillerAvailable.value ? { fillerEnabled: voiceFillerEnabled.value } : {}),
          botEnvironment: voiceBotEnvironment.value,
          channelPrompt: voiceChannelPrompt.value,
          conversationConfig: {
            welcomeMessage: voiceContentType.value === 'message' ? voiceWelcomeMessage.value : '',
            initialFlowId: voiceContentType.value === 'flow' ? voiceInitialFlowId.value : null,
          }
        });
        await loadConnectors();
        toast.add({ severity: 'success', summary: t('botEditor.connectors.toastActivated'), life: 3000 });
      } catch (error) {
        Console.error('[Connectors] Error activating voice connector:', error);
        const code = error.response?.data?.error?.code;
        if (code === 'VAPI_NOT_READY') {
          voiceReady.value = false;
          toast.add({ severity: 'warn', summary: t('botEditor.connectors.voicebot.notReady'), life: 3000 });
        } else if (await handleUnavailable(error)) {
          // gestito: toast + ricarico l'elenco
        } else {
          const serverMessage = extractServerErrorMessage(error);
          toast.add({ severity: 'error', summary: serverMessage || t('botEditor.connectors.toastSaveError'), life: 3000 });
        }
      } finally {
        voiceActionLoading.value = false;
      }
    };

    const deactivateVoice = async () => {
      voiceActionLoading.value = true;
      try {
        await api.post(`/workspaces/${workspaceId}/bots/${botId}/voice/deactivate`);
        await loadConnectors();
        toast.add({ severity: 'success', summary: t('botEditor.connectors.toastDeactivated'), life: 3000 });
      } catch (error) {
        Console.error('[Connectors] Error deactivating voice connector:', error);
        toast.add({ severity: 'error', summary: t('botEditor.connectors.toastError'), life: 3000 });
      } finally {
        voiceActionLoading.value = false;
      }
    };

    // "Prova" is only enabled once the connector is actually active (has a
    // vapiAssistantId, set by the backend on activate). The ephemeral test
    // token is minted server-side on demand (see startVoiceTest).
    const canTestVoice = computed(() => {
      return !!voiceConnector.value?.config?.vapiAssistantId;
    });

    // Perché "Prova" è disabilitato: al momento solo il connettore non attivo.
    const testDisabledReasonKey = computed(() => {
      return !voiceConnector.value?.config?.vapiAssistantId ? 'botEditor.connectors.voicebot.testNeedsActivation' : null;
    });

    const teardownVoiceTest = () => {
      if (vapiInstance) {
        try {
          vapiInstance.stop();
        } catch (error) {
          Console.error('[Connectors] Error stopping Vapi test call:', error);
        }
        try {
          vapiInstance.removeAllListeners();
        } catch (error) {
          // no-op: instance already torn down
        }
        vapiInstance = null;
      }
      callState.value = 'idle';
    };

    const startVoiceTest = async () => {
      if (!canTestVoice.value || callState.value !== 'idle') return;

      const assistantId = voiceConnector.value?.config?.vapiAssistantId;
      transcript.value = [];
      callState.value = 'connecting';

      let token;
      try {
        const response = await api.post(`/workspaces/${workspaceId}/bots/${botId}/voice/test-token`);
        token = response.data?.data?.token;
      } catch (error) {
        Console.error('[Connectors] Error fetching Vapi test token:', error);
        callState.value = 'idle';
        if (error.response?.status === 400) {
          toast.add({ severity: 'warn', summary: t('botEditor.connectors.voicebot.testNotConfigured'), life: 3000 });
        } else {
          toast.add({ severity: 'error', summary: t('botEditor.connectors.toastError'), life: 3000 });
        }
        return;
      }

      try {
        vapiInstance = new Vapi(token);

        vapiInstance.on('call-start', () => {
          callState.value = 'active';
        });
        vapiInstance.on('call-end', () => {
          callState.value = 'idle';
        });
        vapiInstance.on('error', (error) => {
          Console.error('[Connectors] Vapi test call error:', error);
          toast.add({ severity: 'error', summary: t('botEditor.connectors.toastError'), life: 3000 });
          teardownVoiceTest();
        });
        vapiInstance.on('message', (message) => {
          // Skip non-final partial transcripts to avoid flooding the live view.
          if (message?.type === 'transcript' && (!message.transcriptType || message.transcriptType === 'final')) {
            transcript.value = [...transcript.value, { role: message.role, text: message.transcript }];
          }
        });

        await vapiInstance.start(assistantId);
      } catch (error) {
        Console.error('[Connectors] Error starting Vapi test call:', error);
        toast.add({ severity: 'error', summary: t('botEditor.connectors.toastError'), life: 3000 });
        teardownVoiceTest();
      }
    };

    const stopVoiceTest = () => {
      teardownVoiceTest();
    };
    // --- end Voice activation state ---

    const connectorCatalog = computed(() => [
      {
        type: 'web',
        name: t('botEditor.connectors.webWidget.name'),
        icon: 'public',
        color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        description: t('botEditor.connectors.webWidget.description'),
        available: true
      },
      {
        type: 'whatsapp',
        name: t('botEditor.connectors.whatsapp.name'),
        icon: 'whatsapp',
        iconType: 'brand',
        color: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
        description: t('botEditor.connectors.whatsapp.description'),
        available: true
      },
      {
        type: 'voicebot',
        name: t('botEditor.connectors.voicebot.name'),
        icon: 'mic',
        color: 'linear-gradient(135deg, #FF6B6B 0%, #C44569 100%)',
        description: t('botEditor.connectors.voicebot.description'),
        available: true
      },
      {
        type: 'gptlive',
        name: t('botEditor.connectors.gptlive.name'),
        icon: 'record_voice_over',
        color: 'linear-gradient(135deg, #10A37F 0%, #0B6E57 100%)',
        description: t('botEditor.connectors.gptlive.description'),
        available: true
      },
      {
        type: 'livechat',
        name: t('botEditor.connectors.livechat.name'),
        icon: 'support_agent',
        color: 'linear-gradient(135deg, #06B6D4 0%, #0E7490 100%)',
        description: t('botEditor.connectors.livechat.description'),
        available: true
      },
      {
        type: 'telegram',
        name: t('botEditor.connectors.telegram.name'),
        icon: 'telegram',
        iconType: 'brand',
        color: 'linear-gradient(135deg, #0088cc 0%, #005f8c 100%)',
        description: t('botEditor.connectors.telegram.description'),
        available: false
      },
      {
        type: 'msteams',
        name: t('botEditor.connectors.msteams.name'),
        icon: 'microsoft',
        iconType: 'brand',
        color: 'linear-gradient(135deg, #5558AF 0%, #464EB8 100%)',
        description: t('botEditor.connectors.msteams.description'),
        available: false
      }
    ]);

    // Card effettivamente mostrate: filtro per availableTypes (admin), con eccezione
    // per un connettore non più disponibile ma ancora attivo sul bot (unavailableButActive).
    const availableConnectors = computed(() => visibleConnectors(connectorCatalog.value, availableTypes.value, connectors.value));

    const currentConnector = computed(() => {
      return connectorCatalog.value.find(c => c.type === currentConnectorType.value) || {};
    });

    // Options for mode selector
    const modeOptions = computed(() => [
      {
        label: t('botEditor.connectors.configModal.modeProactive'),
        value: 'proactive'
      },
      {
        label: t('botEditor.connectors.configModal.modeReactive'),
        value: 'reactive'
      }
    ]);

    // Options for flow selector
    const flowOptions = computed(() => {
      const options = [
        {
          label: t('botEditor.connectors.configModal.initialFlowNone'),
          value: null
        }
      ];

      if (availableFlows.value.length === 0) {
        options.push({
          label: t('botEditor.connectors.configModal.noFlowsAvailable') || 'Nessun flusso disponibile',
          value: null,
          disabled: true
        });
      } else {
        availableFlows.value.forEach(flow => {
          options.push({
            label: flow.name,
            value: flow._id
          });
        });
      }

      return options;
    });

    // Flussi per WhatsApp: come flowOptions, ma l'opzione "nessun flusso" è
    // etichettata "Non abilitato" (WhatsApp è sempre reattivo, niente messaggio).
    const whatsappFlowOptions = computed(() => {
      const options = [
        { label: t('botEditor.connectors.whatsappActivation.initialFlowNone'), value: null }
      ];
      if (availableFlows.value.length === 0) {
        options.push({
          label: t('botEditor.connectors.configModal.noFlowsAvailable') || 'Nessun flusso disponibile',
          value: null,
          disabled: true
        });
      } else {
        availableFlows.value.forEach(flow => {
          options.push({ label: flow.name, value: flow._id });
        });
      }
      return options;
    });

    // 409 CONNECTOR_UNAVAILABLE: l'admin ha spento il connettore dopo l'apertura
    // della pagina. Avvisa e riallinea l'elenco; ritorna true se l'errore era questo.
    const handleUnavailable = async (error) => {
      if (error.response?.data?.error?.code !== 'CONNECTOR_UNAVAILABLE') return false;
      toast.add({ severity: 'warn', summary: t('botEditor.connectors.unavailableError'), life: 3000 });
      await loadConnectors();
      return true;
    };

    const loadConnectors = async () => {
      connectorsLoading.value = true;
      try {
        const response = await api.get(`/workspaces/${workspaceId}/bots/${botId}/connectors`);
        connectors.value = response.data.data || [];
        availableTypes.value = response.data.availableTypes;
        connectorsLoaded.value = true;
      } catch (error) {
        Console.error('Error loading connectors:', error);
        connectorsLoaded.value = false;
      } finally {
        connectorsLoading.value = false;
      }
    };

    const loadFlows = async () => {
      try {
        // L'endpoint corretto è /flows/:botId
        const response = await api.get(`/workspaces/${workspaceId}/flows/${botId}`);
        // Il backend ritorna: {success: true, data: [array dei flussi]}
        const flows = response.data.data || [];
        availableFlows.value = Array.isArray(flows) ? flows : [];
        Console.log('[Connectors] Loaded flows:', availableFlows.value.length, availableFlows.value);
      } catch (error) {
        Console.error('[Connectors] Error loading flows:', error);
        availableFlows.value = [];
      }
    };

    const isEnabled = (type) => {
      const connector = connectors.value.find(c => c.type === type);
      return connector?.enabled || false;
    };

    const isConfigured = (type) => {
      if (type === 'web') return true;
      const connector = connectors.value.find(c => c.type === type);
      return !!connector;
    };

    const getStatusClass = (type) => {
      if (type === 'whatsapp') return getWhatsappStatusClass();
      if (isEnabled(type)) return '!bg-green-500 !text-white';
      if (isConfigured(type)) return '!bg-blue-500 !text-blue-100';
      return '!bg-gray-600 !text-gray-300';
    };

    const getStatusText = (type) => {
      if (type === 'whatsapp') return getWhatsappStatusText();
      if (isEnabled(type)) return t('botEditor.connectors.statusActive');
      if (isConfigured(type)) return t('botEditor.connectors.statusConfigured');
      return t('botEditor.connectors.statusInactive');
    };

    // Contatore di rimontaggio per card dello switch di attivazione (v. commento nel template).
    const switchNonce = ref({});

    const toggleConnector = async (type, enabled) => {
      try {
        await api.patch(`/workspaces/${workspaceId}/bots/${botId}/connectors/${type}`, { enabled });
        await loadConnectors();
        toast.add({ severity: 'success', summary: enabled ? t('botEditor.connectors.toastActivated') : t('botEditor.connectors.toastDeactivated'), life: 3000 });
      } catch (error) {
        switchNonce.value[type] = (switchNonce.value[type] || 0) + 1;
        Console.error('Error toggling connector:', error);
        if (!(await handleUnavailable(error))) {
          toast.add({ severity: 'error', summary: t('botEditor.connectors.toastError'), life: 3000 });
        }
      }
    };

    // Il toggle della card Live Chat non chiama il PATCH generico (risponde 400
    // USE_DEDICATED_ROUTE): apre il dialog e lo switch resta sullo stato reale.
    // Il click viene annullato prima che lo switch cambi stato: ToggleSwitch tiene uno
    // stato interno, quindi riallineare event.target.checked dopo il change non
    // riporterebbe indietro lo switch (e niente @update:model-value parte per Live Chat).
    const onConnectorSwitchClick = (type, event) => {
      if (type !== 'livechat') return;
      event.preventDefault();
      if (!connectorsLoaded.value) return;
      showLiveChatDialog.value = true;
    };

    const openConfigModal = async (type) => {
      // Senza i connettori caricati non si sa cosa è già configurato: aprire la modal
      // mostrerebbe i default della vista e salvarli li scriverebbe sopra la
      // configurazione reale (card #10278328553). Non serve un toast: il banner in
      // testa alla griglia è già lì, persistente, con il bottone per riprovare.
      if (!connectorsLoaded.value) return;

      // WhatsApp uses its own dedicated activation dialog
      if (type === 'whatsapp') {
        await openWhatsappDialog();
        return;
      }

      // GPT-Live uses its own dedicated activation dialog (readiness gate + form + test call)
      if (type === 'gptlive') {
        showGptLiveDialog.value = true;
        return;
      }

      // Live Chat: dialog dedicato, mai la modal di configurazione generica
      if (type === 'livechat') {
        showLiveChatDialog.value = true;
        return;
      }

      // Voicebot (Vapi) uses its own dedicated activation dialog (readiness gate + form)
      if (type === 'voicebot') {
        await openVoiceDialog();
        return;
      }

      currentConnectorType.value = type;
      
      // Ricarica i flussi quando apri la modal per assicurarti di avere dati freschi
      await loadFlows();
      
      const existing = connectors.value.find(c => c.type === type);

      Console.group('[Connectors] openConfigModal: ' + type);
      if (existing) {
        currentConfig.value = JSON.parse(JSON.stringify(existing));
        // Converti initialFlowId in stringa (MongoDB ritorna ObjectId che diventa un oggetto dopo JSON.stringify)
        if (currentConfig.value.conversationConfig?.initialFlowId) {
          const id = currentConfig.value.conversationConfig.initialFlowId;
          // Se è un oggetto con proprietà _id, estrai quella; altrimenti usa toString
          if (typeof id === 'object' && id !== null) {
            currentConfig.value.conversationConfig.initialFlowId = id._id || id.toString?.() || '';
          } else {
            currentConfig.value.conversationConfig.initialFlowId = String(id);
          }
        }
        // Default botEnvironment per connettori WhatsApp senza valore salvato
        if (type === 'whatsapp') {
          if (!currentConfig.value.config) currentConfig.value.config = {};
          if (!currentConfig.value.config.botEnvironment) {
            currentConfig.value.config.botEnvironment = 'preview';
          }
        }
        Console.log('Existing config:', {
          mode: currentConfig.value.conversationConfig?.mode,
          initialFlowId: currentConfig.value.conversationConfig?.initialFlowId,
          availableFlowIds: availableFlows.value.map(f => f._id)
        });
      } else {
        currentConfig.value = {
          type,
          enabled: false,
          conversationConfig: {
            mode: type === 'web' ? 'proactive' : 'reactive',
            welcomeMessage: type === 'web' ? t('botEditor.connectors.configModal.welcomeMessagePlaceholder') : '',
            initialFlowId: null,
            reenterInitialFlow: false
          },
          config: type === 'whatsapp' ? { botEnvironment: 'preview' } : {},
          channelPrompt: ''
        };
        Console.log('New config created');
      }
      Console.log('Available flows:', availableFlows.value.length);
      Console.groupEnd();

      syncContentTypeFromConfig();
      showConfigModal.value = true;
    };

    const closeConfigModal = () => {
      showConfigModal.value = false;
      currentConnectorType.value = null;
    };

    const saveConnectorConfig = async () => {
      try {
        applyContentXor(); // messaggio XOR flusso: azzera il campo non scelto
        await api.post(`/workspaces/${workspaceId}/bots/${botId}/connectors`, currentConfig.value);
        await loadConnectors();
        closeConfigModal();
        toast.add({ severity: 'success', summary: t('botEditor.connectors.toastSaved'), life: 3000 });
      } catch (error) {
        Console.error('Error saving connector:', error);
        toast.add({ severity: 'error', summary: t('botEditor.connectors.toastSaveError'), life: 3000 });
      }
    };

    onMounted(async () => {
      await loadBotData();
      await loadConnectors();
      await loadFlows();
      await loadWhatsappStatus();
    });

    onBeforeUnmount(() => {
      teardownVoiceTest();
    });

    return {
      workspaceId,
      botId,
      availableConnectors,
      connectors,
      connectorsLoaded,
      connectorsLoading,
      loadConnectors,
      availableFlows,
      showConfigModal,
      currentConnectorType,
      currentConnector,
      currentConfig,
      modeOptions,
      flowOptions,
      whatsappFlowOptions,
      isEnabled,
      isConfigured,
      getStatusClass,
      getStatusText,
      switchNonce,
      toggleConnector,
      openConfigModal,
      closeConfigModal,
      saveConnectorConfig,
      contentType,
      startContentEmpty,
      hasEmptyStartConfig,
      loadFlows,
      // Bot environment
      productionBotId,
      botEnvironmentOptions,
      // WhatsApp activation
      showWhatsappDialog,
      whatsappStatus,
      whatsappPhoneInput,
      whatsappPhoneError,
      whatsappLoading,
      closeWhatsappDialog,
      requestWhatsapp,
      goToTemplates,
      saveWhatsappConfig,
      whatsappChannelPromptInvalid,
      // WhatsApp delivery webhook (Configurazione tecnica)
      deliveryWebhookUnavailable,
      fetchDeliveryWebhook,
      saveDeliveryWebhookConfig,
      onDeliveryWebhookLoadError,
      // GPT-Live activation dialog
      showGptLiveDialog,
      gptLiveConnector,
      // Live Chat dialog
      showLiveChatDialog,
      botName,
      liveChatConnector,
      onConnectorSwitchClick,
      // Voice (Vapi) activation
      showVoiceDialog,
      voiceReady,
      voiceAvailabilityLoading,
      voiceActionLoading,
      voicesLoading,
      selectedVoiceKey,
      voiceGroups,
      voiceCurrent,
      voiceFillerAvailable,
      voiceFillerEnabled,
      voiceFillerWarn,
      voiceChannelPrompt,
      voiceChannelPromptInvalid,
      voiceBotEnvironment,
      voiceContentType,
      voiceWelcomeMessage,
      voiceInitialFlowId,
      voiceFlowOptions,
      isVoiceActive,
      closeVoiceDialog,
      activateVoice,
      deactivateVoice,
      // Voice test call ("Prova")
      callState,
      transcript,
      canTestVoice,
      testDisabledReasonKey,
      startVoiceTest,
      stopVoiceTest,
      // Telephony (SIP trunk / Twilio number linking)
      telephonyProvider,
      telephonySipGateway,
      telephonySipUsername,
      telephonySipPassword,
      telephonyTwilioAccountSid,
      telephonyTwilioAuthToken,
      telephonyNumber,
      telephonyActionLoading,
      telephonyProviderOptions,
      telephonyLinkedNumber,
      telephonyLinkedProviderLabel,
      sipPasswordPlaceholder,
      twilioAuthTokenPlaceholder,
      linkTelephony,
      unlinkTelephony
    };
  }
};
</script>

<style scoped>
.connectors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

.connector-card {
  background: #374151;
  border: 2px solid #4b5563;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s;
}

.connector-card:hover {
  border-color: #6b7280;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.connector-card.is-enabled {
  border-color: #48bb78;
  background: #1a3a2a;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.connector-icon-container {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.connector-info {
  flex: 1;
}

.connector-info h3 {
  font-size: 16px;
  font-weight: 600;
  color: #f7fafc;
  margin-bottom: 4px;
}

.card-body {
  padding-top: 16px;
  border-top: 1px solid #4b5563;
}

.connector-description {
  color: #d1d5db;
  font-size: 14px;
  margin-bottom: 16px;
  line-height: 1.5;
}

/* Presidio: configurazione di avvio vuota su un connettore attivo (card #10278328553).
   Solo spaziatura: colore, tipografia e icona vengono dal preset (severity="warn",
   variant="simple"), così l'avviso resta nel vocabolario della superficie. */
.connector-start-warning {
  margin-bottom: 16px;
}

/* Coming Soon Overlay */
.connector-card.is-unavailable {
  pointer-events: none;
}

.coming-soon-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(55, 65, 81, 0.8); /* #374151 with 80% opacity */
  border-radius: 12px; /* Match border radius of card */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(1px);
}
</style>
